// Labels e mapas de UI para o módulo Diagnóstico.
// Mantenha esta lista alinhada com /lib/diagnostico-score.js (fonte canônica das verificações).

// Labels entendíveis em PT-BR. A ordem alfabética é aplicada na renderização
// (sortVerificacoes) — esse map só armazena o texto.
export const VERIFICACAO_LABEL: Record<string, string> = {
  // Pessoa
  bndes: 'BNDES — Operações ativas',
  ccpf: 'Receita Federal — Cadastro CPF',
  ceis: 'CEIS / CNEP — Sanções administrativas',
  cnj: 'CNJ — Improbidade e inelegibilidade',
  cndt: 'CNDT — Certidão de Débitos Trabalhistas',
  ibama_embargo: 'IBAMA — Embargos por CPF',
  pep: 'PEP — Pessoa Politicamente Exposta',
  scr_bacen: 'SCR Bacen — Posição financeira',
  // Pessoa/Geo compartilhado
  proagro: 'PROAGRO — Adesões e sinistros',
  safra: 'Plano Safra — Beneficiários',
  sicafi: 'SICAFI — Autuações IBAMA',
  sinaflor: 'SINAFLOR — Autuações florestais',
  // Geo
  car_status_onr: 'CAR Status — Cadastro Ambiental Rural (ONR)',
  ccir_externo: 'CCIR — Cadastro INCRA',
  cnfp: 'CNFP — Cadastro Florestas Públicas',
  embargo_estadual: 'SEDAM — Embargos estaduais (RO)',
  ibama_embargo_geo: 'IBAMA — Embargos por geometria',
  icmbio_autos: 'ICMBio — Autos de infração',
  icmbio_embargos: 'ICMBio — Embargos',
  prodes: 'PRODES — Desmatamento Amazônia',
  sicar: 'SICAR — Cadastro Ambiental Rural',
  sigef: 'SIGEF — Certificação georreferenciada',
  sobreposicao: 'Sobreposição — Terra Indígena / Assentamento / UC',
  // Internas
  car: 'CAR — Cadastro Ambiental Rural',
  ccir: 'CCIR — Certificado de Cadastro de Imóvel Rural',
  certidao: 'Certidão de Inteiro Teor',
  caf: 'CAF — Cadastro Agricultura Familiar',
  lic_ambiental: 'Licença Ambiental',
  // Sintética (ONR consultado em paralelo, mesclado na lista de verificações)
  registro_imoveis: 'Registro Eletrônico de Imóveis',
};

export function labelVerificacao(code: string): string {
  return VERIFICACAO_LABEL[code] ?? code.replace(/_/g, ' ').toUpperCase();
}

// Ordena verificações por label PT-BR (locale-aware: respeita acentos).
export function sortVerificacoes<T extends { verificacao: string }>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    labelVerificacao(a.verificacao).localeCompare(labelVerificacao(b.verificacao), 'pt-BR'),
  );
}

// Verificações ocultadas da UI (back-end ainda processa, não exibimos).
const VERIFICACOES_OCULTAS = new Set(['itr', 'itr_externo', 'itr_nirf']);

export function filterVerificacoes<T extends { verificacao: string }>(items: T[]): T[] {
  return items.filter((v) => !VERIFICACOES_OCULTAS.has(v.verificacao));
}

export const NIVEL_LABEL = {
  critico: 'Crítico',
  atencao: 'Atenção',
  regular: 'Regular',
} as const;

// Mapeamento status → tom de UI (chip / icon).
export type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info';

export function statusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (s === 'regular' || s === 'ok') return 'ok';
  if (s === 'irregular') return 'danger';
  if (s === 'atencao' || s === 'alerta') return 'warn';
  if (s === 'pendente') return 'warn';
  return 'neutral';
}

export function nivelTone(nivel: string): Tone {
  if (nivel === 'critico') return 'danger';
  if (nivel === 'atencao') return 'warn';
  if (nivel === 'regular') return 'ok';
  return 'neutral';
}

// Verificações que têm endpoint de drill-in com mapa Leaflet renderizado pelo back.
interface MapEndpoint {
  path: string;
  requiresImovel: boolean;
  label: string;
}

const MAP_ENDPOINTS: Record<string, MapEndpoint> = {
  prodes: { path: 'prodes-detalhe', requiresImovel: true, label: 'Mapa PRODES' },
  cnfp: { path: 'cnfp-detalhe', requiresImovel: true, label: 'Mapa CNFP' },
  ibama_embargo: {
    path: 'ibama-embargo-detalhe',
    requiresImovel: false,
    label: 'Detalhes IBAMA',
  },
  ibama_embargo_geo: {
    path: 'ibama-embargo-geo-detalhe',
    requiresImovel: true,
    label: 'Mapa IBAMA (geo)',
  },
  embargo_estadual: {
    path: 'embargo-estadual-detalhe',
    requiresImovel: true,
    label: 'Mapa embargo estadual',
  },
  icmbio_autos: {
    path: 'icmbio-autos-detalhe',
    requiresImovel: true,
    label: 'Mapa ICMBio · autos',
  },
  icmbio_embargos: {
    path: 'icmbio-embargos-detalhe',
    requiresImovel: true,
    label: 'Mapa ICMBio · embargos',
  },
  sobreposicao: {
    path: 'sobreposicao-detalhe',
    requiresImovel: true,
    label: 'Mapa sobreposição',
  },
};

export interface MapTarget {
  url: string;
  label: string;
}

export function mapTargetFor(
  verificacao: string,
  cpf: string,
  imovelId: string | null,
): MapTarget | null {
  const cfg = MAP_ENDPOINTS[verificacao];
  if (!cfg) return null;
  if (cfg.requiresImovel && !imovelId) return null;
  const qs = imovelId ? `?imovel=${encodeURIComponent(imovelId)}` : '';
  return {
    url: `/api/v2/diagnostico/${encodeURIComponent(cpf)}/${cfg.path}${qs}`,
    label: cfg.label,
  };
}
